import { LANG } from '@/libs/translation';
import { Input, InputChangeEvent, Typo } from '@/ui/component/atom';
import { filter, last, map, pipe, take, toArray } from '@fxts/core';
import { $, $Element, CustomEventWithDetail, html, View } from 'rune-ts';
import klass from './AutoCompleteEmail.module.scss';

export interface AutoCompleteEmailData {}

interface AutoCompleteDomainListData {}

class AutoCompleteDomainItemClick extends CustomEventWithDetail<{ value: string }> {}

class AutoCompleteDomainListView extends View<AutoCompleteDomainListData> {
  private list: string[];
  constructor(data: AutoCompleteDomainListData) {
    super(data);
    this.list = [];
  }

  override template() {
    return html`
      <div class="${klass.list}" data-is-show="${!!this.list.length}">
        ${this.list.map((domain) => {
          return html` <div class="${klass.item}" data-value="${domain}">${new Typo({ text: domain })}</div>`;
        })}
      </div>
    `;
  }

  setList(email: string) {
    if (email.includes('@')) {
      const [local_part = '', domain = ''] = email.split('@');
      this.list = pipe(
        DOMAINS[LANG],
        filter((auto_domain) => {
          if (domain) {
            try {
              return new RegExp(`^${domain}`).test(auto_domain);
            } catch (_e) {
              return false;
            }
          }
          return true;
        }),
        map((domain) => {
          return local_part + '@' + domain;
        }),
        take(7),
        toArray,
      );
    } else {
      this.list = [];
    }
  }

  getList() {
    return this.list;
  }

  show() {
    this.element().dataset.isShow = 'true';
  }

  isShow() {
    return this.element().dataset.isShow === 'true';
  }

  hide() {
    this.element().dataset.isShow = 'false';
  }

  afterRedraw(height: number) {
    this.element().style.top = height - 1 + 'px';
  }

  override onRender() {
    this.delegate('click', `.${klass.item}`, (e) => {
      this.dispatchEvent(AutoCompleteDomainItemClick, {
        detail: { value: (e.currentTarget as HTMLElement)?.dataset.value ?? '' },
        bubbles: true,
      });
    });
  }
}

export class AutoCompleteEmailView extends View<AutoCompleteEmailData> {
  views: {
    email$: Input;
    domainList$: AutoCompleteDomainListView;
  };

  private last_value: string;

  constructor(data: AutoCompleteEmailData, email$: Input) {
    super(data);
    this.views = {
      email$,
      domainList$: new AutoCompleteDomainListView({}),
    };
    this.last_value = '';
  }

  private chaneDomainList() {
    const value = this.views.email$.value ?? '';
    this.last_value = value;
    this.views.domainList$.setList(value);
    this.views.domainList$.redraw();
    this.views.domainList$.afterRedraw(this.views.email$.inputBaseView.element().offsetHeight);
  }

  selectedItemInputEmailValue(item$: $Element | undefined | null) {
    if (item$) {
      item$.element().dataset.selected = 'true';
      this.views.email$.value = item$.element().dataset.value ?? '';
    } else {
      this.views.email$.value = this.last_value;
    }
  }

  override onRender() {
    this.delegate(InputChangeEvent, Input, () => this.chaneDomainList());
    this.delegate('focusin', Input, () => {
      this.chaneDomainList();
      if (this.views.domainList$.getList()?.length) {
        return this.views.domainList$.show();
      }
    });
    this.delegate('focusout', Input, () => {
      setTimeout(() => {
        this.views.domainList$.hide();
      }, 500);
    });
    this.delegate('keydown', Input, (e) => {
      if (!this.views.domainList$.getList()?.length) {
        return;
      }

      const items$ = $(this.views.domainList$.element()).findAll(`.${klass.item}`);
      const selected_item$ = $(this.views.domainList$.element()).find(`.${klass.item}[data-selected="true"]`);
      if (selected_item$) {
        selected_item$.element().dataset.selected = '';
      }

      if (e.key === 'Enter') {
        if (selected_item$) {
          this.selectedItemInputEmailValue(selected_item$);
          this.chaneDomainList();
        }

        return this.views.domainList$.hide();
      }

      /*
       * 엔터가 아닌데 키다운이면 리스트를 보여줘야한다.
       * */
      if (!this.views.domainList$.isShow()) {
        return this.views.domainList$.show();
      }

      if (e.key === 'ArrowDown') {
        if (selected_item$) {
          const next$ = $(selected_item$.element()).next(`.${klass.item}`);
          this.selectedItemInputEmailValue(next$);
        } else {
          const first_item$ = items$[0];
          this.selectedItemInputEmailValue(first_item$);
        }
      }
      if (e.key === 'ArrowUp') {
        e.originalEvent.preventDefault();
        if (selected_item$) {
          const prev$ = $(selected_item$.element()).prev(`.${klass.item}`);
          this.selectedItemInputEmailValue(prev$);
        } else {
          const last_item$ = last(items$);
          this.selectedItemInputEmailValue(last_item$);
        }
      }
    });

    this.delegate(AutoCompleteDomainItemClick, AutoCompleteDomainListView, (e) => {
      const value = e.detail.value;
      if (value) {
        this.views.email$.value = value;
        this.chaneDomainList();
      }
      this.views.domainList$.hide();
    });
  }

  override template() {
    return html` <div class="${klass.wrapper}">${this.views.email$}${this.views.domainList$}</div>`;
  }
}

const KR_DOMAINS = [
  'naver.com',
  'gmail.com',
  'daum.net',
  'hanmail.net',
  'nate.com',
  'kakao.com',
  'marpple.com',
  'yahoo.co.kr',
  'kornet.net',
  'paran.com',
  'empal.com',
  'sayclub.com',
  'hanmir.com',
  'freechal.com',
  'dreamwiz.com',
  'lycos.co.kr',
  'korea.com',
  'samsung.com',
  'skku.edu',
  'postech.ac.kr',
  'yonsei.ac.kr',
  'kyunghee.ac.kr',
  'pusan.ac.kr',
  'sogang.ac.kr',
  'kaist.ac.kr',
  'snu.ac.kr',
  'ajou.ac.kr',
  'ewha.ac.kr',
  'uos.ac.kr',
  'chonnam.ac.kr',
  'chungbuk.ac.kr',
  'daegu.ac.kr',
  'dongguk.ac.kr',
  'hanyang.ac.kr',
  'konkuk.ac.kr',
  'korea.ac.kr',
  'kyu.ac.kr',
  'kw.ac.kr',
  'kyungpook.ac.kr',
  'myongji.ac.kr',
  'sejong.ac.kr',
  'seoultech.ac.kr',
  'sungshin.ac.kr',
  'wku.ac.kr',
  'yeungnam.ac.kr',
  'catholic.ac.kr',
  'cu.ac.kr',
  'du.ac.kr',
  'hongik.ac.kr',
  'inha.ac.kr',
  'jbnu.ac.kr',
  'cbnu.ac.kr',
  'knu.ac.kr',
  'kms.ac.kr',
  'kmu.ac.kr',
  'kwu.ac.kr',
  'kangwon.ac.kr',
  'point.edu',
  'jsu.ac.kr',
  'jeonju.ac.kr',
  'jj.ac.kr',
  'jnu.ac.kr',
  'kbh.ac.kr',
  'kcu.ac.kr',
  'kmcu.ac.kr',
  'ks.ac.kr',
  'ksu.ac.kr',
  'kyungnam.ac.kr',
  'niu.ac.kr',
  'saturn.kr',
  'sehan.ac.kr',
  'spu.ac.kr',
  'ulsan.ac.kr',
  'curvature.kr',
  'ollehmail.com',
  'omega.kr',
  'open-institute.ac.kr',
  'pu.ac.kr',
  'samyook.ac.kr',
  'sookmyung.ac.kr',
  'soongsil.ac.kr',
  'sudokwon.ac.kr',
  'sungkyul.ac.kr',
  'taejon.ac.kr',
  'thomas.ac.kr',
  'univtech.ac.kr',
  'usb.ac.kr',
  'wsu.ac.kr',
  'ybu.ac.kr',
  'yuhan.ac.kr',
  'yooman.ac.kr',
  'ynu.ac.kr',
  'ytu.ac.kr',
];

const EN_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'outlook.com',
  'aol.com',
  'hotmail.com',
  'icloud.com',
  'marpple.com',
  'mail.com',
  'live.com',
  'msn.com',
  'comcast.net',
  'verizon.net',
  'sbcglobal.net',
  'earthlink.net',
  'att.net',
  'me.com',
  'mac.com',
  'cox.net',
  'charter.net',
  'bellsouth.net',
  'frontier.com',
  'mindspring.com',
  'netzero.net',
  'juno.com',
  'peoplepc.com',
  'optonline.net',
  'centurytel.net',
  'suddenlink.net',
  'twc.com',
  'roadrunner.com',
  'buckeye-express.com',
  'mediacomtoday.com',
  'wowway.com',
  'mtco.com',
  'zoominternet.net',
  'dishmail.net',
  'usa.net',
  'windstream.net',
  'rcn.com',
  'seawolftechnologies.com',
  'yandex.com',
  'inbox.com',
  'fastmail.com',
  'ureach.com',
  'mailfence.com',
  'hughes.net',
  'surewest.net',
  'netzero.com',
  'aim.com',
  'pacbell.net',
  'myway.com',
  'gmx.com',
  'protonmail.com',
  'hushmail.com',
  'zoho.com',
  'laposte.net',
  'wildblue.net',
  'googlemail.com',
  'embarqmail.com',
  'sina.com',
  'lineage.com',
  'zeo.net',
  'camden.rutgers.edu',
  'berklee.edu',
  'engadgetonline.com',
  'hillsboroughcollege.edu',
  'reading.ac.uk',
  'lycos.com',
  'blueyonder.co.uk',
  'alaska.edu',
  'mobile.globe.com.ph',
  'virginia.edu',
  'ithaca.edu',
  'cts.com',
  'brightok.net',
  'attglobal.net',
  'email.com',
  'globalcrossing.com',
  'ca.rr.com',
  'gci.net',
  'stenbergcollege.com',
  'vol.com',
  'earthcam.net',
  'epbfi.com',
  'cheerioconnect.com',
  'sctcs.edu',
  'cmungusemail3.com',
  'drillsimione.com',
  'ezweb.net',
  'farmerstel.com',
  'frontiernet.net',
  'lakeheadu.ca',
  'nscc.ca',
  'rcn.net',
  'triad.rr.com',
  'schusterman.org',
  'sympatico.ca',
  'us.army.mil',
  'ymail.com',
  'zz.com',
  'rediffmail.com',
];

const JP_DOMAINS = [
  'yahoo.co.jp',
  'gmail.com',
  'docomo.ne.jp',
  'au.com',
  'ezweb.ne.jp',
  'i.softbank.jp',
  'marpple.com',
  'biglobe.ne.jp',
  'nifty.com',
  'ocn.ne.jp',
  'ymobile.ne.jp',
  'plala.or.jp',
  'goo.ne.jp',
  'sonet.ne.jp',
  'rakuten.jp',
  'excite.co.jp',
  'nifty.ne.jp',
  'dion.ne.jp',
  'livedoor.com',
  'mineo.jp',
  'kddi.com',
  'linemobile.com',
  'm.vodafone.ne.jp',
  'jcom.zaq.ne.jp',
  'niftyserve.or.jp',
  'freeml.com',
  'jupiter.ocn.ne.jp',
  'jcom.home.ne.jp',
  'internet.plala.or.jp',
  'ctc-web.co.jp',
  'lazylabrador.jp',
  'en.mysteriousbox.com',
  'v-equitylife.co.jp',
  'postman.go.jp',
  'koshitsu.co.jp',
  'gifu-u.ac.jp',
  'shinshu-u.ac.jp',
  'osaka-u.ac.jp',
  'keio-u.ac.jp',
  'waseda.jp',
  'kyushu-u.ac.jp',
  'u-tokyo.ac.jp',
  'hiroshima-u.ac.jp',
  'bomx03.zaq.ne.jp',
  'j-tlo.co.jp',
  'tsukuba.ac.jp',
  'ajinomoto.co.jp',
  'kasai.co.jp',
  'aquarios.co.jp',
  'ehime-u.ac.jp',
  'nl.cyberhome.ne.jp',
];

const DOMAINS = {
  kr: KR_DOMAINS,
  en: EN_DOMAINS,
  jp: JP_DOMAINS,
};
